/* ***  VERSION 1.0.0  *** */

// Common classes, methods and constants
import * as JWT from '../jwt/jwt.js';  // JWT token parsing
import * as Login from '../login/login.js';  // Login form rendering
import * as TopBar from '../topbar/topbar.js';  // Top bar rendering

// The <script> tag ID
const tagID = 'ecosystemScript';

// Thresholds, in seconds
const checkThreshold = 5;  // JWT check: every 5 seconds
const pingThreshold = 60;  // Ping API: every 60 seconds

// Messages
const msgSessionExpired = 'Your session has expired.';
const msgScriptTagNotFound = 'Ecosystem :: Script tag not found';
const msgLoginUrlNotFound = 'Ecosystem :: Login URL not found';
const msgApiUrlNotFound = 'Ecosystem :: API URL not found';

// Global token name and API URL
export var tokenName = 'token';
export var apiUrl = false;

/**
 * Helper function to make API calls.
 *
 * @param    {string}   method    The HTTP method (e.g., 'GET', 'POST')
 * @param    {string}   endpoint  The API endpoint URL, relative to the API URL
 * @param    {object}   data      Optional data to send with the request (for POST/PUT)
 * @returns  {Promise}            A promise that resolves with the API response data or rejects with an error message
 */
export function apiCall(method, endpoint, data = null)
{
	// Prepare fetch options
	const options = {
		method: method,
		headers: {
			'Authorization': 'Bearer ' + Cookies.get(tokenName),
			'Content-Type': 'application/json',
		},
	};

	// Attach data to the request if provided
	if (data)
	{
		if (method === 'GET') url += (url.includes('?') ? '&' : '?') + new URLSearchParams(data).toString();
		else options.body = JSON.stringify(data);
	}

	// Make the API call and handle the response
	return fetch(apiUrl + endpoint, options)

		// Parse JSON; if body is not JSON (e.g., 404, 500) fall through to catch
		.then(response => response.json().catch(() => ({
			success: false,
			message: response.status + ' ' + response.statusText
		})))

		// Handle API json response
		.then(data =>
		{
			// If the response is not successful, throw an error
			if (!data.success) throw new Error(data.message);

			// Successful response, return the data
			return data;
		});
}

/**
 * Ecosystem class
 * @class Ecosystem
 */
export class Ecosystem
{
	/**
	 * Class constructor
	 */
	constructor()
	{
		// Script URLs
		this.loginUrl = false;
		this.currentUrl = false;

		// JWT token and uid
		this.jwt = false;
		this.uid = false;

		// Session expired <div>
		this.alert = false;

		// Startup the whole process
		this.startup();
	}

	/**
	 * Perform the startup check to determine the current status
	 */
	startup()
	{
		// Check if the <script> tag is present
		if (!this._checkTag())
		{
			console.log(msgScriptTagNotFound);
			return;
		}

		// Get the login URL from the <script> tag
		this.loginUrl = this._getLoginUrl();
		if (!this.loginUrl)
		{
			console.log(msgLoginUrlNotFound);
			return;
		}

		// Get the API URL from the <script> tag
		apiUrl = this._getApiUrl();
		if (!apiUrl)
		{
			console.log(msgApiUrlNotFound);
			return;
		}
		else
		{
			// Copmplete the token name (since the name changes depending on environment)
			let matches = apiUrl.match(/^https?:\/\/([a-z0-9-]+)\.[a-z0-9-]+\.wowcast\.co/i);
			if (matches) tokenName += '-' + matches[1];
		}

		// Get the currect URL (without query string)
		this.currentUrl = this._getCurrentUrl();

		// If we are in the login page render the login form
		if (this.currentUrl == this.loginUrl)
		{
			if (document.readyState != 'loading') Login.setup(apiUrl);
			else document.addEventListener('DOMContentLoaded', () => Login.setup(apiUrl));
		}

		// Else we are in an internal page
		else
		{
			// Start the token check loop
			this._checkToken();
			setInterval(() => this._checkToken(), checkThreshold * 1000);

			// Start the ping to API loop
			setTimeout(() => this._pingApi(), 1000);
			setInterval(() => this._pingApi(), pingThreshold * 1000);

			// Build the top bar
			if (document.readyState != 'loading') TopBar.build(this.jwt, apiUrl);
			else document.addEventListener('DOMContentLoaded', () => TopBar.build(this.jwt, apiUrl));
		}
	}

	/**
	 * Helper function to make API calls.
	 *
	 * @param    {string}   method    The HTTP method
	 * @param    {string}   endpoint  The API endpoint
	 * @param    {object}   data      The request data
	 * @returns  {Promise}            A promise that resolves with the API response data or rejects with an error message
	 */
	apiCall(method, endpoint, data = null) { return apiCall(method, endpoint, data); }

	/**
	 * Perform the token check
	 */
	_checkToken()
	{
		// Expired flag
		var expired = true;

		// Get and check the token
		this.jwt = JWT.get();
		if (this.jwt)
		{
			// If the uid of the token has changed, reload the page (user logged with another account)
			if (this.uid && (this.uid != this.jwt.uid))
			{
				window.onbeforeunload = null;  // Clear the onbeforeunload handler (if there)
				document.location.reload();
				return;
			}
			this.uid = this.jwt.uid;

			// Calculate the token expiration time
			var deltaTime = this.jwt.exp - Date.now() / 1000;
			if (deltaTime > checkThreshold) expired = false;
		}

		// If the token is expired
		if (expired)
		{
			if (this.alert) { } // Nothing to do
			else this.alert = this._showModalAlert();  // Show modal alert
		}

		// If the token is NOT expired
		else
		{
			if (this.alert) this.alert.cancel();  // Destroy modal alert
			else { } // Nothing to do
		}
	}

	/**
	 * Ping the API
	 */
	_pingApi()
	{
		// Call the server
		fetch(apiUrl + '/v1/auth/ping',
			{
				method: 'POST',
				headers: { 'Authorization': 'Bearer ' + Cookies.get(tokenName) }
			})

			// Parse JSON; if body is not JSON (e.g. 404, 500) fall through to catch
			.then(response => response.json()
				.then(data => data)
				.catch(() => Promise.reject(response))
			)

			// Handle positive response
			.then(response =>
			{
				// If server responded with an error -> delete token
				if (!response.success) Cookies.delete(tokenName, { domain: response.data.token_domain, path: '/' });
			})

			// Handle communication errors
			.catch(error => console.error('Ping ::', error));
	}

	/**
	 * Create the alert for the expired session
	 * @returns  {HTMLElement}  The modal alert element
	 */
	_showModalAlert()
	{
		// Create the modal alert
		asyncAlert(msgSessionExpired)         // Inform about session expiration
			.then(() => this._goToLogin())     // When user clicks "OK", redirect to the login page
			.catch(() => this.alert = false);  // If user logs-in from another tab, modal is canceled -> Reset the alert variable

		// Return the modal element
		return document.querySelector('.asyncAlert');
	}

	/**
	 * Redirects the user to the login page with a query string indicating session expiration.
	 * The query string includes an encoded error message and the current URL for redirection after login.
	 */
	_goToLogin()
	{
		// Clear the onbeforeunload handler (if there)
		window.onbeforeunload = null;

		// Mount login querystring
		var querystring = ['m=' + encodeURIComponent(msgSessionExpired), 'u=' + encodeURIComponent(this.currentUrl)].join('&');
		document.location = this.loginUrl + '?' + querystring;
	}

	/**
	 * Check if the <script> tag is present
	 * @returns  {boolean}  True if the tag is present, false otherwise
	 */
	_checkTag() { return document.getElementById(tagID) ? true : false; }

	/**
	 * Get the current URL (without query string)
	 * @returns  {string}  The current URL
	 */
	_getCurrentUrl() { return window.location.href.split('?')[0]; }

	/**
	 * Get the login URL from the <script> tag
	 * @returns  {string}  The login URL
	 */
	_getLoginUrl() { return document.getElementById(tagID).dataset.login; }

	/**
	 * Get the API URL from the <script> tag
	 * @returns  {string}  The API URL
	 */
	_getApiUrl() { return document.getElementById(tagID).dataset.api; }
}

